<template>
  <q-tab-pane name="general">
    <!-- Team Logo -->
    <div class="row gutter-x-md justify-start">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('logo') }}</h6>
        <p class="text-faded">{{ $t('logo_hover') }}</p>
      </div>

      <div class="col-md-9 col-12">
        <image-input
          :uploadEndpoint="storeLogo"
          :deleteEndpoint="deleteLogo"
          :imageUrl="teamLogo"
          :imageType="$t('logo')"
        >
          <team-logo slot="placeholder" :team="team" class="q-display-3" height="160px" width="160px"/>
        </image-input>
      </div>
    </div>

    <hr/>

    <!-- Team Data -->
    <div class="row gutter-x-md">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $tc('team') }}</h6>
        <p class="text-faded">{{ $t('team_hint') }}</p>
      </div>

      <div class="col-md-9 col-12">
        <!-- Team Name -->
        <q-field
          :label="$t('team_name')"
          :labelWidth="3"
          :error="errors.has('name')"
          :error-label="errors.first('name')"
        >
          <q-input
            v-model="formData.name"
            v-validate.disable="'required|min:1|max:255'"
            name="name"
            :data-vv-as="$t('team_name')"
            :placeholder="$t('team_name')"
          />
        </q-field>

        <!-- Legal Form -->
        <q-field
          :label="$t('legal_form')"
          :labelWidth="3"
          :error="errors.has('legal_form')"
          :error-label="errors.first('legal_form')"
        >
          <q-select
            v-model="formData.legalForm"
            :options="legalFormOptions"
            v-validate.disable="'required'"
            name="legal_form"
            :data-vv-as="$t('legal_form')"
            :placeholder="$t('legal_form')"
          />
        </q-field>

        <!-- Tax Number -->
        <q-field
          :label="$t('tax_number')"
          :labelWidth="3"
          :error="errors.has('tax_number')"
          :error-label="errors.first('tax_number')"
        >
          <q-input
            v-model="formData.taxNumber"
            v-validate.disable="'min:1|max:255'"
            name="tax_number"
            :data-vv-as="$t('tax_number')"
            :placeholder="$t('tax_number')"
          />
        </q-field>

        <!-- VAT Number -->
        <q-field
          :label="$t('vat_number')"
          :labelWidth="3"
          :error="errors.has('vat_number')"
          :error-label="errors.first('vat_number')"
        >
          <q-input
            v-model="formData.vatNumber"
            v-validate.disable="'min:1|max:255'"
            name="vat_number"
            :data-vv-as="$t('vat_number')"
            :placeholder="$t('vat_number')"
          />
        </q-field>

        <!-- Registration Number -->
        <q-field
          :label="$t('registration_number')"
          :labelWidth="3"
          :error="errors.has('registration_number')"
          :error-label="errors.first('registration_number')"
        >
          <q-input
            v-model="formData.registrationNumber"
            v-validate.disable="'min:1|max:255'"
            name="registration_number"
            :data-vv-as="$t('registration_number')"
            :placeholder="$t('registration_number')"
          />
        </q-field>

        <!-- Registration Court -->
        <q-field
          :label="$t('registration_court')"
          :labelWidth="3"
          :error="errors.has('registration_court')"
          :error-label="errors.first('registration_court')"
        >
          <q-input
            v-model="formData.registrationCourt"
            v-validate.disable="'min:1|max:255'"
            name="registration_court"
            :data-vv-as="$t('registration_court')"
            :placeholder="$t('registration_court')"
          />
        </q-field>

        <!-- Managers -->
        <q-field
          :label="$t('managers')"
          :labelWidth="3"
        >
          <q-chips-input
            ref="managerInput"
            v-model="formData.managers"
            name="managers"
            add-icon="add"
            :placeholder="$t('managers')"
            @blur="handleManagerBlur"
          />
        </q-field>
      </div>
    </div>

    <hr/>

    <div class="row gutter-x-md">
      <div class="col-md-3 col-12">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('language') }}</h6>
        <p class="text-faded">{{ $t('language_hint') }}</p>
      </div>

      <div class="col-md-9 col-12">
        <!-- Language -->
        <language-input
          v-model="formData.language"
          :labelWidth="3"
        ></language-input>

        <!-- Small Business -->
        <q-field>
          <q-checkbox v-model="formData.isSmallBusiness" :label="$t('small_business')" :disable="team.isSubscribedToStudentPlan()">
            <q-tooltip v-if="team.isSubscribedToStudentPlan()">{{ $t('small_business_info_student') }}</q-tooltip>
          </q-checkbox>
          <q-icon name="info" color="faded" class="on-right">
            <q-tooltip>{{ $t('small_business_info') }}</q-tooltip>
          </q-icon>
        </q-field>
      </div>
    </div>

    <div class="row justify-end">
      <q-btn type="submit" color="primary" @click="handleSave">{{ $t('save') }}</q-btn>
    </div>
  </q-tab-pane>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import CurrencyInput from 'components/Common/Inputs/CurrencyInput.vue'
  import ImageInput from 'components/Common/Inputs/ImageInput.vue'
  import LanguageInput from 'components/Common/Inputs/LanguageInput.vue'
  import TeamLogo from 'components/Common/TeamLogo.vue'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        formData: {
          name: '',
          taxNumber: '',
          vatNumber: '',
          registrationNumber: '',
          registrationCourt: '',
          legalForm: '',
          managers: [],
          language: '',
          defaultCurrency: '',
          isSmallBusiness: false
        }
      }
    },

    computed: {
      ...mapGetters(['team']),

      teamLogo () {
        return this.team.settings.logo_url
      },

      legalFormOptions () {
        return [
          { label: this.$t('legal_forms.freelancer.title'), value: 'freelancer' },
          { label: this.$t('legal_forms.sole.title'), value: 'sole' },
          { label: this.$t('legal_forms.partnership.title'), value: 'partnership' },
          { label: this.$t('legal_forms.corporation.title'), value: 'corporation' },
          { label: this.$t('legal_forms.other.title'), value: 'other' }
        ]
      }
    },

    methods: {
      ...mapActions(['uploadLogo', 'removeLogo', 'updateTeam']),

      storeLogo (logo) {
        return this.uploadLogo({ teamId: this.team.id, logo })
      },

      deleteLogo () {
        return this.removeLogo(this.team.id)
      },

      handleSave () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return
            }

            this.storeChanges()
          })
      },

      storeChanges () {
        this.updateTeam({
          teamId: this.team.id,
          team: { ...changeCase.snake(this.formData), ...{ managers: this.formData.managers } }
        })
          .then(() => {
            this.$q.notify({ message: this.$t('team_settings_save_success'), type: 'positive' })
          })
          .catch(createErrorFromResponse)
      },

      handleManagerBlur (data) {
        // Hack to commit value without user pressing enter
        const val = this.$refs.managerInput.input
        this.$refs.managerInput.add(val)
      }
    },

    created () {
      this.formData = {
        name: this.team.name,
        taxNumber: this.team.tax_number,
        vatNumber: this.team.vat_number,
        registrationNumber: this.team.registration_number,
        registrationCourt: this.team.registration_court,
        legalForm: this.team.legal_form,
        managers: this.team.managers ? this.team.managers : [],
        language: this.team.settings.language,
        isSmallBusiness: this.team.settings.is_small_business
      }
    },

    components: {
      CurrencyInput,
      LanguageInput,
      ImageInput,
      TeamLogo
    }
  }
</script>
